import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useEffect, useCallback, useId } from 'react';
import { ChevronLeftIcon } from '@iris/react-icons/ChevronLeftIcon';
import { Root, Trigger, DialogPortal, Overlay, Content, Title, Description } from '@radix-ui/react-dialog';
import { ControlButton } from '../ControlButton/ControlButton.esm.js';
import { fullscreenSheetStyles } from './FullscreenSheet.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

const FullscreenSheet = forwardRef((_a, ref) => {
    var { trigger, headerContent, footerContent, children, className, bodyClassName, contentClassName, headerClassName, footerClassName, fixedFooter = true, showBackButton = true, onBackClick, backButtonAriaLabel = "Go back", breakpoint = "md", open, defaultOpen, onOpenChange, onAnimationStart, onOpenAutoFocus } = _a, dialogProps = __rest(_a, ["trigger", "headerContent", "footerContent", "children", "className", "bodyClassName", "contentClassName", "headerClassName", "footerClassName", "fixedFooter", "showBackButton", "onBackClick", "backButtonAriaLabel", "breakpoint", "open", "defaultOpen", "onOpenChange", "onAnimationStart", "onOpenAutoFocus"]);
    const contentRef = useRef(null);
    const [localOpen, setLocalOpen] = useState(() => { var _a; return (_a = open !== null && open !== void 0 ? open : defaultOpen) !== null && _a !== void 0 ? _a : false; });
    useEffect(() => {
        if (typeof open === "boolean") {
            setLocalOpen(open);
        }
    }, [open]);
    const actualOpen = open !== null && open !== void 0 ? open : localOpen;
    const handleOpenChange = useCallback((isOpen) => {
        setLocalOpen(isOpen);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(isOpen);
    }, [onOpenChange]);
    const handleOpenAutoFocus = useCallback((event) => {
        var _a;
        event.preventDefault();
        (_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        onOpenAutoFocus === null || onOpenAutoFocus === void 0 ? void 0 : onOpenAutoFocus(event);
    }, [onOpenAutoFocus]);
    const handleBackButtonClick = useCallback(() => {
        onBackClick === null || onBackClick === void 0 ? void 0 : onBackClick();
        handleOpenChange(false);
    }, [handleOpenChange, onBackClick]);
    const headerId = useId();
    const bodyId = useId();
    const { overlay, wrapper, content, header, headerInner, backButton, backIcon, headerSlot, body, footer, } = fullscreenSheetStyles({ fixedFooter, breakpoint });
    const contentClasses = cn(content(), className, contentClassName);
    const overlayClasses = cn(overlay());
    const bodyClasses = cn(body(), bodyClassName);
    const headerClasses = cn(header(), headerClassName);
    const footerClasses = cn(footer(), footerClassName);
    const rootProps = Object.assign(Object.assign({}, dialogProps), { onOpenChange: handleOpenChange });
    if (typeof open === "boolean") {
        rootProps.open = open;
    }
    else {
        rootProps.open = localOpen;
        if (typeof defaultOpen === "boolean") {
            rootProps.defaultOpen = defaultOpen;
        }
    }
    return (jsxs(Root, Object.assign({}, rootProps, { modal: true, children: [trigger && (jsx(Trigger, { asChild: true, "aria-controls": actualOpen ? bodyId : undefined, children: trigger })), jsxs(DialogPortal, { children: [jsx(Overlay, { className: overlayClasses }), jsx("div", { className: cn(wrapper()), children: jsxs(Content, { ref: (node) => {
                                contentRef.current = node;
                                if (typeof ref === "function") {
                                    ref(node);
                                }
                                else if (ref) {
                                    ref.current = node;
                                }
                            }, className: contentClasses, tabIndex: -1, "aria-labelledby": headerContent ? headerId : undefined, "aria-describedby": bodyId, "data-state": actualOpen ? "open" : "closed", onAnimationStart: onAnimationStart, onOpenAutoFocus: handleOpenAutoFocus, children: [jsx("header", { className: headerClasses, children: jsxs("div", { className: headerInner(), children: [showBackButton && (jsx(ControlButton, { className: backButton(), icon: jsx(ChevronLeftIcon, { className: backIcon() }), "aria-label": backButtonAriaLabel, onClick: handleBackButtonClick })), jsx(Title, { asChild: true, children: jsx("div", { id: headerId, className: headerSlot(), children: headerContent }) })] }) }), jsx(Description, { "aria-hidden": true }), jsxs("div", { className: bodyClasses, id: bodyId, "aria-live": "polite", "aria-atomic": "true", children: [children, !fixedFooter && (jsx("footer", { className: footerClasses, children: footerContent }))] }), fixedFooter && (jsx("footer", { className: footerClasses, children: footerContent }))] }) })] })] })));
});
FullscreenSheet.displayName = "FullscreenSheet";

export { FullscreenSheet };
